import Store from 'app/redux/store'
import { cloneDeep } from 'lodash'
import { reorder } from 'app/lib/utility'
import { applyUnverifiedSources, findUnverifiedSources } from 'app/lib/funnel-template'
import { bindActions } from 'redux-zero/utils'
import { BusinessManagerStage as Stage } from '@marketing-milk/interfaces'

const Funnel = bindActions(
  store => ({
    // Override (For testing)
    override: (state: any, funnel) => ({
      pages: {
        ...state.pages,
        business: {
          ...state.pages.business,
          funnel: funnel,
        },
      },
    }),

    // Reset aka go back to preset selector
    reset: state => ({
      pages: {
        ...state.pages,
        business: {
          ...state.pages.business,
          funnel: {
            active: null,
            preset: 0,
            stages: [],
          },
        },
      },
    }),

    // Set Preset
    // `acceptedReplacements` and `acceptedKeeps` are what the user checked in
    // ApplyTemplateDialog. Every other unverified per-business source is
    // removed, and every one not replaced is marked on its group, so the
    // dialog's list is exactly what happens here.
    setPreset: (
      state,
      id: number,
      acceptedReplacements: string[] = [],
      acceptedKeeps: string[] = []
    ) => {
      const match = state.data.funnel.presets.data.find(x => x.id === id)
      // A stale id is expected, not exceptional: a template can be deleted, or
      // the business opened by a different user, while `funnel_preset` still
      // points at it. Without this the `.find()` miss threw on `.stages`.
      if (!match) return state

      // Deep-cloned, not assigned by reference: StageActions mutates stages in
      // place, and these are cached API objects the user may apply again.
      const stages = cloneDeep(match.stages)
      applyUnverifiedSources(
        stages,
        findUnverifiedSources(match, state.data.funnel.sources),
        new Set(acceptedReplacements),
        new Set(acceptedKeeps)
      )

      return {
        pages: {
          ...state.pages,
          business: {
            ...state.pages.business,
            funnel: {
              active: 0,
              preset: id,
              stages,
            },
          },
        },
      }
    },

    // Add Blank Stage
    addStage: state => {
      let stages: Stage[] = [].concat(state.pages.business.funnel.stages)

      stages.push({
        name: 'New Stage',
        desc: 'This is a blank stage ready to pull data',
        icon: 'smile',
        support: ['conversions'],
        groups: [],
        estimate: false,
        order: 100,
        conversion_name: '',
        est_conversion_name: '',
        show_total: true,
        is_kpi: false,
        is_sub_kpi: false,
        show_overview: true,
        cost_per_metric: 1,
      })

      store.setState({
        pages: {
          ...state.pages,
          business: {
            ...state.pages.business,
            funnel: {
              ...state.pages.business.funnel,
              stages: stages,
              active: stages.length - 1,
            },
          },
        },
      })
    },

    // Focus on Stage
    focusStage: (state, index: number | null) => ({
      pages: {
        ...state.pages,
        business: {
          ...state.pages.business,
          funnel: {
            ...state.pages.business.funnel,
            active: index,
          },
        },
      },
    }),

    // Re order stage
    changeOrder: (state, prior: number, updated: number) => {
      let stages: Stage[] = [].concat(state.pages.business.funnel.stages)
      const { active } = state.pages.business.funnel
      const current = stages[active]
      stages = reorder(stages, prior, updated)

      for (let i = 0; i < stages.length; i++) {
        stages[i].order = i
      }

      store.setState({
        pages: {
          ...state.pages,
          business: {
            ...state.pages.business,
            funnel: {
              ...state.pages.business.funnel,
              active: active !== null ? stages.findIndex(x => x == current) : null,
              stages: stages,
            },
          },
        },
      })
    },
  }),
  Store
)

export default Funnel
